import * as React from 'react';
import styled from 'styled-components';
import axios from 'axios';
import errorReporter from '@app/errorReporter';
import { OrgFeatures, VirtualTourJsonType } from '@bb/domain/dist/types';
import { VirtualTourController } from 'api';
import { formatTimeStr, formatISODateStr } from 'utils';
import { message } from 'components';
import VirtualTourListTab from '../listing/appointments/virtual_tour_list/VirtualTourListTab';
import AppointmentPdfBody from './appointment_pdf_body';

type VirtualTour = VirtualTourJsonType & {
  displayStatus: string;
};

interface Report {
  headers: string[];
  values: string[][];
  creator: string;
  location: string;
  userName: string;
  teamName: string;
  dateFrom: string;
  dateTo: string;
  group: string;
  listingId?: string;
  userId?: string;
  teamId?: string;
  filteredFeatures: OrgFeatures[];
}
interface Question {
  label: string;
  response: string;
}

interface Appointment {
  id: string;
}

interface Feedback {
  template: Question[];
  appointment: Appointment;
}

interface FeedbacksObject {
  [appointmentId: string]: Feedback;
}

interface ListingFeedbacks {
  feedbacks: Feedback[];
}

interface Props {
  reportId: string;
}

interface State {
  report: Report;
  notListing: boolean;
  showContactInfo: boolean;
  showFeedback: boolean;
  feedback: FeedbacksObject;
  tabKey: tabKey;
  listingId: string;
  userId: string;
  teamId: string;
  virtualTourValues: any[][];
  hasFeature: (feature: OrgFeatures) => boolean;
}

type tabKey = 'inPersonShowing' | 'virtualShowing';
export default class AppointmentPDFComponent extends React.Component<
  Props,
  State
> {
  constructor(props: any) {
    super(props);
    this.state = {
      report: null,
      notListing: false,
      showContactInfo: true,
      showFeedback: true,
      feedback: null,
      tabKey: 'inPersonShowing',
      listingId: null,
      userId: null,
      teamId: null,
      virtualTourValues: [],
      hasFeature: (feature: OrgFeatures) => false,
    };
  }

  async componentDidMount() {
    try {
      // in person appointments
      const { reportId } = this.props;
      const { data } = await axios.get<Report>(
        `/external/appointment_reports/${reportId}`,
      );
      const { listingId, userId, teamId, filteredFeatures } = data;
      const { group, dateFrom, dateTo } = data;
      delete data.listingId;
      delete data.userId;
      delete data.teamId;

      const hasFeature = (feature: OrgFeatures) =>
        filteredFeatures.includes(feature);

      const report = data;
      this.setState({ report, listingId, userId, teamId, hasFeature });

      let feedbackUrl;
      if (group === 'TeamDetails' || group === 'UserDetails') {
        this.setState({ notListing: true });
        let paramUrl = group === 'TeamDetails' ? '?group=team' : '?group=user';
        if (data.dateFrom) paramUrl += `&dateFrom=${data.dateFrom}`;
        if (data.dateTo) paramUrl += `&dateTo=${data.dateTo}`;
        feedbackUrl = `/appointment/feedbacks${paramUrl}`;
      } else {
        feedbackUrl = `/listing/${listingId}/feedbacks`;
      }

      const {
        data: { feedbacks },
      } = await axios.get<ListingFeedbacks>(feedbackUrl);
      const feedbackObj: FeedbacksObject = {};

      // Map feedbacks to object keyed by appointment id
      feedbacks.forEach(feedback => {
        feedbackObj[feedback.appointment.id] = feedback;
      });
      this.setState({ feedback: feedbackObj });

      let virtualTourFetchType: 'listing' | 'user' | 'team' = 'listing';
      let modelId = listingId;
      if (group === 'TeamDetails' && teamId) {
        virtualTourFetchType = 'team';
        modelId = teamId;
      } else if (group === 'UserDetails' && userId) {
        virtualTourFetchType = 'user';
        modelId = userId;
      }

      if (modelId && hasFeature('VIRTUAL_SHOWINGS')) {
        const { virtualTours } =
          await VirtualTourController.getDateFilteredVirtualTours({
            modelId,
            type: virtualTourFetchType,
            dateFrom,
            dateTo,
          });
        const virtualTourValues = this.processVirtualTours(virtualTours);
        this.setState({ virtualTourValues });
      }
    } catch (err) {
      errorReporter.error(`Error fetching appointment PDF data: ${err}`);
      message.error('Error generating appointment PDF data');
    }
  }

  processVirtualTours(virtualTours: Array<VirtualTour>) {
    return virtualTours
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      )
      .map(tour => {
        const { startTime, coop, displayStatus, timezone } = tour;
        return [
          formatISODateStr(startTime, timezone),
          formatTimeStr(startTime, timezone),
          coop.name,
          coop?.org?.name,
          coop.email,
          coop.phone,
          coop?.org?.phone,
          displayStatus,
        ];
      });
  }

  handleShowContactInfoChange = (e: React.SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    this.setState({
      showContactInfo: target.checked,
    });
  };

  handleShowFeedbackChange = (e: React.SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    this.setState({
      showFeedback: target.checked,
    });
  };

  handleTabChange = (key: tabKey) => {
    this.setState({
      tabKey: key,
    });
  };

  // Translates index to appointmentId and finds associated feedback
  findFeedbackByAppointmentIndex = (appointmentIndex: number) => {
    // Return null if data isn't all present so the JSX gives the no feedback message
    // Technically report and feedback should always be safe though, because the
    // component should show only the spinner anyway
    const { report, feedback } = this.state;
    if (!report || !report.values || !feedback) return null;

    const lastIndex = report.headers.length - 1;
    const appointmentId = report.values[appointmentIndex][lastIndex];
    return feedback[appointmentId];
  };

  render() {
    // Loading spinner
    const {
      report,
      feedback,
      tabKey,
      virtualTourValues,
      notListing,
      showContactInfo,
      showFeedback,
      hasFeature,
    } = this.state;
    if (!(report && feedback)) {
      return (
        <div className="normalheader transition animated fadeIn">
          <div className="hpanel">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-2 col-md-offset-5 spin">
                  <i className="fa fa-spinner fa-spin" />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const appointments =
      tabKey === 'inPersonShowing' ? report.values : virtualTourValues;

    const reindex = notListing ? 1 : 0;

    return (
      <div>
        <div
          className="hidden-print"
          style={{
            minWidth: '8.5in',
            maxWidth: '8.5in',
            margin: '10px auto',
            display: 'flex',
            flexFlow: 'row nowrap',
          }}
        >
          {/* Hide email button until functionality exists */}
          {/* <button
            className="btn btn-primary"
            onClick={() => {
              // TODO: Implement email click handler once functionality exists
            }}
            style={{ marginRight: '1em' }}
          >
            Email
          </button> */}

          {/* Until we upgrade typescript this window as any is necessary */}
          <button
            className="btn btn-success"
            onClick={(window as Window).print}
            style={{ marginRight: '3em' }}
          >
            Print PDF
          </button>

          {/* Include checkboxes */}
          <div style={{ display: 'flex', flexFlow: 'row wrap' }}>
            <b style={{ flex: '1 1 100%' }}>Include:</b>

            {/* Show Contact Info checkbox */}
            <div style={{ paddingRight: '3em' }}>
              <label
                className="form-check-label"
                htmlFor="contact-info-checkbox"
                style={{
                  paddingLeft: '0',
                  marginBottom: '0',
                  fontWeight: 'normal',
                }}
              >
                Showing Agent Contact Info
              </label>
              <input
                type="checkbox"
                id="contact-info-checkbox"
                className="form-check-input pull-right"
                checked={showContactInfo}
                onChange={this.handleShowContactInfoChange}
                style={{
                  // Override bootstrap to pull checkbox right of label
                  position: 'static',
                  marginLeft: '1.5em',
                }}
              />
            </div>

            {/* Show Feedback checkbox */}
            <div style={{ paddingRight: '3em' }}>
              <label
                className="form-check-label"
                htmlFor="feedback-checkbox"
                style={{
                  paddingLeft: '0',
                  marginBottom: '0',
                  fontWeight: 'normal',
                }}
              >
                Showing Feedback
              </label>
              <input
                type="checkbox"
                id="feedback-checkbox"
                className="form-check-input pull-right"
                checked={showFeedback}
                onChange={this.handleShowFeedbackChange}
                style={{
                  // Override bootstrap to pull checkbox right of label
                  position: 'static',
                  marginLeft: '1.5em',
                }}
              />
            </div>
          </div>
        </div>
        <div
          className="hidden-print"
          style={{
            minWidth: '8.5in',
            maxWidth: '8.5in',
            margin: '10px auto',
          }}
        >
          <VirtualTourListTab
            setActionTab={this.handleTabChange}
            hasVSFeatureFlag={hasFeature('VIRTUAL_SHOWINGS')}
          />
        </div>

        {/* Page */}
        <div className="hpanel">
          <StyledDiv className="panel-body print-page">
            {/**
             * Page elements are wrapped in a table because it's one of the
             * only ways to reliably get a header and footer onto each page in
             * print media.
             *
             * This is necessary as unlike in the appointment reportpage, the
             * elements of this pdf can have wildly varying heights, e.g.
             * somebody sending a wall of text in a single message; this way we
             * let print handle the page breaks for us.
             */}
            <table>
              {/* Page header */}
              <thead className="print-header">
                <tr>
                  <td>
                    <div className="row">
                      <div className="col-xs-7">
                        <h3 style={{ marginTop: '0' }}>
                          <strong>
                            {tabKey === 'virtualShowing' ? 'Virtual ' : ''}
                            Showing Activity Report
                          </strong>
                        </h3>
                        <h3>
                          {report.location ||
                            report.userName ||
                            report.teamName}
                        </h3>
                        <br />
                      </div>
                      <div className="col-xs-5">
                        <div className="row">
                          <div className="col-xs-6">Created By:</div>
                          <div className="col-xs-6">{report.creator}</div>
                        </div>
                        <div className="row">
                          <div className="col-xs-6">Date of Report:</div>
                          <div className="col-xs-6">
                            {new Date().toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </thead>

              {/* Page body */}
              <AppointmentPdfBody
                appointments={appointments}
                showContactInfo={showContactInfo}
                notListing={notListing}
                reindex={reindex}
                showFeedback={showFeedback}
                findFeedbackByAppointmentIndex={
                  this.findFeedbackByAppointmentIndex
                }
                isVirtualTour={tabKey === 'virtualShowing'}
              />

              {/* Page footer */}
              <tfoot className="print-footer">
                <tr>
                  <td>
                    <div>
                      {/**
                       * Blank footer but the css is in place to use it to
                       * define page margins.
                       */}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </StyledDiv>
        </div>
      </div>
    );
  }
}

const StyledDiv = styled.div`
  @media print {
    &&::before,
    &&::after {
      content: none;
    }
  }
  min-width: 8.5in;
  max-width: 8.5in;
  margin: auto;
`;
